/* eslint-disable react/jsx-no-target-blank */
import "./Footer.scss"
import { ImFacebook, ImLinkedin2 } from "react-icons/im";
import { FaInstagram } from "react-icons/fa";
import { FiArrowRight, FiChevronDown } from "react-icons/fi";
import { Link, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from "react";
import { OptimizedImage } from "../../atoms/OptimizedImage/OptimizedImage";

const FORM_ROUTES = [
    "/contacto",
    "/subscripcion",
    "/subscripcion/true",
    "/aliados/siigo",
    "/aliados/siigoPos",
    "/aliados/treli",
    "/index.php/invitacion-2/",
    "/diagnostico/test",
    "/download/guide",
];

// Base del API de Functions (fetch plano SIN token — el endpoint
// /footerMenuV2/public es público). Mismo mecanismo que PublicRoutePage.
const API_BASE = process.env.REACT_APP_API_URL;

// Fallback resiliente: si el fetch al menú falla o un grupo viene vacío, se
// renderizan estos links. El sitio NUNCA debe quedar con footer sin links.
// Refleja la semilla del backend (functions/controllers/footerMenu.controller.js
// → DEFAULT_FOOTER_ITEMS). Runtime distinto (ESM vs CommonJS): no se comparte.
const DEFAULT_FOOTER_MENU = {
    navegacion: [
        { label: "Inicio", path: "/", external: false },
        { label: "Productos", path: "/productos", external: false },
        { label: "Plataforma", path: "/plataforma", external: false },
        { label: "Nosotros", path: "/nosotros", external: false },
        { label: "Blog", path: "/blog", external: false },
    ],
    aliados: [
        { label: "Siigo", path: "/aliados/siigo", external: false },
        { label: "Siigo POS", path: "/aliados/siigoPos", external: false },
        { label: "Treli", path: "/aliados/treli", external: false },
        { label: "Diagnóstico", path: "/diagnostico/test", external: false },
        { label: "Guía gratuita", path: "/download/guide", external: false },
    ],
    legal: [
        { label: "Aviso de privacidad", path: "/politicas_condiciones", external: false },
        { label: "Tratamiento de datos", path: "/tratamiento_de_datos_personales", external: false },
        { label: "Términos y condiciones", path: "/terminos_condiciones_shofi", external: false },
    ],
};

const GROUPS = ["navegacion", "aliados", "legal"];

// Fallback de los títulos de columna (texto hardcodeado histórico).
const DEFAULT_FOOTER_TITLES = {
    navegacion: "Navegación",
    aliados: "Aliados",
    legal: "Legal",
};

// Renderiza un ítem: externo → <a target="_blank">, interno → <Link>.
const renderLink = (item, i) => {
    const key = `${item.path}-${item.label}-${i}`;
    if (item.external) {
        return <a key={key} href={item.path} target="_blank" rel="noopener noreferrer">{item.label}</a>;
    }
    return <Link key={key} to={item.path}>{item.label}</Link>;
};

export const Footer = () => {
    const [openSection, setOpenSection] = useState(null);
    const [visible, setVisible] = useState(false);
    const [menu, setMenu] = useState(DEFAULT_FOOTER_MENU);
    const [titles, setTitles] = useState(DEFAULT_FOOTER_TITLES);
    const footerRef = useRef(null);
    const location = useLocation();
    const hideCta = FORM_ROUTES.includes(location.pathname) || location.pathname.startsWith("/gracias/");

    const toggleSection = (section) => {
        setOpenSection(prev => prev === section ? null : section);
    };

    // Menú del footer data-driven (gestionado por SA-001/SA-005). Fallback al
    // menú por defecto si falla el fetch o un grupo viene vacío.
    useEffect(() => {
        if (!API_BASE) return;
        let cancelled = false;
        fetch(`${API_BASE}/footerMenuV2/public`)
            .then(res => (res.ok ? res.json() : null))
            .then(data => {
                if (cancelled || !data) return;
                if (data.menu) {
                    const merged = {};
                    GROUPS.forEach(g => {
                        const arr = Array.isArray(data.menu[g]) ? data.menu[g] : [];
                        merged[g] = arr.length ? arr : DEFAULT_FOOTER_MENU[g];
                    });
                    setMenu(merged);
                }
                if (data.titles) {
                    const mergedTitles = {};
                    GROUPS.forEach(g => {
                        mergedTitles[g] = data.titles[g] || DEFAULT_FOOTER_TITLES[g];
                    });
                    setTitles(mergedTitles);
                }
            })
            .catch(() => { /* el fallback ya está en el estado inicial */ });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const el = footerRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setTimeout(() => setVisible(true), 600);
                        observer.disconnect();
                    }
                });
            },
            { threshold: 0.15 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <footer
            ref={footerRef}
            className={`footer${visible ? " footer--visible" : ""}`}
        >
            {/* ─── CTA Section ─── */}
            <section className="footer-cta">
                <div className="footer-cta-content">
                    <h2 className="footer-cta-title">
                        ¿Listo para transformar el<br />trabajo remoto de tu equipo?
                    </h2>
                    <div className="footer-cta-buttons">
                        {!hideCta && (
                            <Link to="/contacto" className="btn-primary-footer">
                                Agenda tu cita
                            </Link>
                        )}
                        <Link to="/login" className="btn-secondary-footer">
                            Ingresa a la plataforma <FiArrowRight />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ─── Main Footer ─── */}
            <section className="footer-main">
                <div className="footer-main-content">
                    {/* Brand */}
                    <div className="footer-col footer-col-brand">
                        <OptimizedImage
                            className="footer-logo"
                            alt="SHOFI"
                            src="/images/brands/logo-shofi-blanco.png"
                            width={140}
                            height={40}
                        />
                        <p className="footer-tagline">Tu aliado experto para<br />el trabajo en casa</p>
                        <div className="footer-social">
                            <a href="https://www.linkedin.com/company/shofi/" target="_blank" aria-label="LinkedIn"><ImLinkedin2 /></a>
                            <a href="https://www.facebook.com/shoficolombia" target="_blank" aria-label="Facebook"><ImFacebook /></a>
                            <a href="https://www.instagram.com/shofi_colombia/" target="_blank" aria-label="Instagram"><FaInstagram /></a>
                        </div>
                    </div>

                    {/* Navegación */}
                    <div className={`footer-col footer-col-collapsible ${openSection === 'nav' ? 'open' : ''}`}>
                        <button className="footer-col-title" onClick={() => toggleSection('nav')}>
                            {titles.navegacion} <FiChevronDown className="chevron-icon" />
                        </button>
                        <nav className="footer-nav">
                            {menu.navegacion.map(renderLink)}
                        </nav>
                    </div>

                    {/* Aliados */}
                    <div className={`footer-col footer-col-collapsible ${openSection === 'aliados' ? 'open' : ''}`}>
                        <button className="footer-col-title" onClick={() => toggleSection('aliados')}>
                            {titles.aliados} <FiChevronDown className="chevron-icon" />
                        </button>
                        <nav className="footer-nav">
                            {menu.aliados.map(renderLink)}
                        </nav>
                    </div>

                    {/* Legal */}
                    <div className={`footer-col footer-col-collapsible ${openSection === 'legal' ? 'open' : ''}`}>
                        <button className="footer-col-title" onClick={() => toggleSection('legal')}>
                            {titles.legal} <FiChevronDown className="chevron-icon" />
                        </button>
                        <nav className="footer-nav">
                            {menu.legal.map(renderLink)}
                        </nav>
                    </div>
                </div>
            </section>

            {/* ─── Copyright ─── */}
            <section className="footer-bottom">
                <p>© {new Date().getFullYear()} SHOFI SAS — Bogotá D.C., Colombia</p>
            </section>
        </footer>
    );
}
